import { useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useServer, useServers } from '@/hooks/use-servers';
import { frameCopy, pageLimits } from '@/components/layout/frame-copy';
import { useSignoff } from '@/components/layout/signoff';
import { serverDetailId, type AsideMode } from '@/lib/page-frame';
import { pickUrgentHost, quoteNpcArg } from '@/lib/spatial-route';
import type { Server } from '@/api/types';

function CopyLine({ cmd }: { cmd: string }) {
  const { i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const [fallback, setFallback] = useState<string | null>(null);
  const write = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      toast.success(copy.copied);
      setFallback(null);
    } catch {
      setFallback(cmd);
      toast.error(copy.copyFailed);
    }
  };
  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-3">
        <code className="tri-cmd min-w-0 flex-1">$ {cmd}</code>
        <button type="button" className="np-btn" onClick={() => void write()}>
          {copy.copyAgent}
        </button>
      </div>
      {fallback ? (
        <textarea className="np-fallback" readOnly value={fallback} onFocus={(event) => event.currentTarget.select()} />
      ) : null}
    </div>
  );
}

function AskCard({ serverId }: { serverId?: string }) {
  const { i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const { waiting, openSheet } = useSignoff();
  const mine = serverId ? waiting.filter((cert) => cert.serverId === serverId) : waiting;
  const current = mine[0] ?? null;
  return (
    <section>
      <h2>
        {copy.signoff} <b>{mine.length}</b>
      </h2>
      {current ? (
        <button type="button" className="ask-line np-crop np-crop-signal" onClick={() => openSheet(current.id)}>
          <span>{copy.rebind}</span>
          <code>{current.primaryDomain}</code>
          <small>{copy.review}</small>
        </button>
      ) : (
        <p className="none">{copy.noneBody}</p>
      )}
    </section>
  );
}

function OverviewAgent() {
  const { i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const { data } = useServers({ page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' });
  const urgent = pickUrgentHost(data?.data ?? []);
  const cmds = ['npc server list'];
  if (urgent) {
    cmds.push(`npc server handoff brief ${quoteNpcArg(urgent.name)} --section agentSummary`);
  }
  return (
    <section>
      <h2>{copy.agent}</h2>
      <p className="mb-3 text-[13px] text-[var(--ink-2)]">{copy.agentStart}</p>
      <div className="grid gap-3">
        {cmds.map((cmd) => (
          <CopyLine key={cmd} cmd={cmd} />
        ))}
      </div>
    </section>
  );
}

function agentSteps(server: Server, copy: ReturnType<typeof frameCopy>): Array<[string, string]> {
  const name = quoteNpcArg(server.name);
  if (server.status === 'offline' || server.status === 'unknown') {
    return [[copy.offlineWhy, `npc server handoff brief ${name} --section agentSummary`]];
  }
  if (server.status === 'pending_probe') {
    return [[copy.probeWhy, `npc server probe install ${name}`]];
  }
  return [
    [copy.briefWhy, `npc server handoff brief ${name} --section agentSummary`],
    [copy.playbookWhy, `npc server playbook list ${name}`],
  ];
}

function ServerAgent({ id }: { id: string }) {
  const { i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const { data: server } = useServer(id);
  if (!server) return null;
  const steps = agentSteps(server, copy);
  return (
    <section>
      <h2>{copy.agent}</h2>
      <ol className="np-next">
        {steps.map(([why, cmd], index) => (
          <li key={cmd}>
            <p>
              <b>{String(index + 1).padStart(2, '0')}</b>
              {why}
            </p>
            <CopyLine cmd={cmd} />
          </li>
        ))}
      </ol>
      <p className="mt-3 text-[12px] text-[var(--ink-3)]">{copy.agentNote}</p>
    </section>
  );
}

function Limits() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const copy = frameCopy(i18n.language);
  const limits = pageLimits(pathname, i18n.language);
  return (
    <section>
      <h2>{copy.limits}</h2>
      <ul className="limits">
        {limits.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </section>
  );
}

export function PageAside({ mode }: { mode: AsideMode }) {
  const { pathname } = useLocation();
  const { waiting } = useSignoff();
  const serverId = mode === 'server' ? serverDetailId(pathname) : null;
  const showAsk = mode !== 'server' || waiting.some((cert) => cert.serverId === serverId);
  return (
    <>
      {showAsk ? (
        <div className="np-aside np-aside-ask">
          <AskCard serverId={serverId ?? undefined} />
        </div>
      ) : null}
      <div className="np-aside np-aside-rest">
        {mode === 'overview' ? <OverviewAgent /> : null}
        {mode === 'server' && serverId ? <ServerAgent id={serverId} /> : null}
        {mode === 'overview' ? null : <Limits />}
      </div>
    </>
  );
}

export function LimitsFoot() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const copy = frameCopy(i18n.language);
  const limits = pageLimits(pathname, i18n.language);
  if (limits.length === 0) return null;
  return (
    <p className="limits-foot">
      <b>{copy.limits}</b>
      {limits.map((line, index) => (
        <span key={line}>
          {index > 0 ? <i aria-hidden="true"> · </i> : null}
          {line}
        </span>
      ))}
    </p>
  );
}
